import { useState, useEffect } from "react";
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { usePermissions } from "@/hooks/use-permissions";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Star, CreditCard, AlertTriangle, Paperclip, Loader2 } from "lucide-react";
import { annotateAttachmentUrl } from "@/lib/attachment-url";
import { AttachmentPreviews } from "@/components/message-attachments";
import { ApproveTaskDialog } from "@/components/approve-task-dialog";
import type { OperationsTask, Vendor, Property } from "@shared/schema";
import { computeVendorFeeBreakdown, netVendorPayoutAmount } from "@shared/fees";
import { StatusBadge } from "@/pages/app-financials/CardsTab";
import { uploadMimeType } from "@/lib/upload-mime";

type MercuryCardLite = {
  id: number;
  nickname: string | null;
  last4: string | null;
  mercuryCardStatus: string | null;
  assignedPropertyId: number | null;
};

function formatCents(cents: number) {
  return `$${(cents / 100).toFixed(2)}`;
}

function formatCentsOpt(cents: number | null | undefined) {
  if (!cents) return "$0.00";
  return `$${(cents / 100).toFixed(2)}`;
}

function parseManualMethod(value: string): { method: string; cardId: number | null } | null {
  if (!value) return null;
  if (value.startsWith("card:")) {
    const id = parseInt(value.slice(5), 10);
    return isNaN(id) ? null : { method: "card", cardId: id };
  }
  return { method: value, cardId: null };
}

function dollarsToCents(value: string): number | null {
  const n = parseFloat(value);
  if (isNaN(n) || n < 0) return null;
  return Math.round(n * 100);
}

function StarRating({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onClick={() => onChange(star)}
          className="focus:outline-none"
          data-testid={`star-${star}`}
        >
          <Star
            className={`h-6 w-6 transition-colors ${
              star <= value ? "text-yellow-400 fill-yellow-400" : "text-gray-200 hover:text-yellow-200"
            }`}
          />
        </button>
      ))}
    </div>
  );
}

function PriceBreakdownLines({ task, testId }: { task: OperationsTask; testId: string }) {
  const breakdown = (task as any).priceBreakdown as {
    rateType?: "hourly" | "flat";
    rateCents?: number;
    hours?: number;
    laborCents?: number;
    flatCents?: number;
    travelFeeCents?: number;
  } | null | undefined;
  const lockedRateType = (task as any).lockedRateType as "hourly" | "flat" | null | undefined;
  const lockedRateCents = ((task as any).lockedRateCents as number | null | undefined) ?? null;
  const taskTravelFeeCents = ((task as any).travelFeeCents as number | null | undefined) ?? 0;
  const rateType = breakdown?.rateType ?? lockedRateType ?? null;
  if (!rateType) return null;
  const travelFeeCents = breakdown?.travelFeeCents ?? taskTravelFeeCents ?? 0;
  const totalCents = task.priceCents ?? 0;
  return (
    <div className="text-xs text-gray-500 flex flex-wrap gap-x-4 gap-y-0.5" data-testid={testId}>
      {rateType === "hourly" ? (
        <>
          <span data-testid={`${testId}-rate-type`}>Rate type: Hourly</span>
          <span data-testid={`${testId}-rate`}>
            Rate: ${(((breakdown?.rateCents ?? lockedRateCents) || 0) / 100).toFixed(2)}/hr
          </span>
          {breakdown?.hours != null && <span data-testid={`${testId}-hours`}>Hours worked: {breakdown.hours}</span>}
          {breakdown?.laborCents != null && <span data-testid={`${testId}-labor`}>Labor: {formatCentsOpt(breakdown.laborCents)}</span>}
        </>
      ) : (
        <>
          <span data-testid={`${testId}-rate-type`}>Rate type: Flat</span>
          <span data-testid={`${testId}-flat`}>Flat rate: {formatCentsOpt(breakdown?.flatCents ?? lockedRateCents ?? 0)}</span>
        </>
      )}
      {travelFeeCents > 0 && <span data-testid={`${testId}-travel`}>Travel fee: {formatCentsOpt(travelFeeCents)}</span>}
      <span className="font-medium text-gray-700" data-testid={`${testId}-final`}>Final amount: {formatCentsOpt(totalCents)}</span>
    </div>
  );
}

function ManualProofField({
  urls, uploading, readOnly, onSelect, onRemove, idPrefix,
}: {
  urls: string[]; uploading: boolean; readOnly: boolean;
  onSelect: (files: FileList | null) => void; onRemove: (idx: number) => void; idPrefix: string;
}) {
  return (
    <div>
      <label className="text-sm font-medium text-gray-700 block mb-1">Proof of payment (optional)</label>
      <p className="text-xs text-gray-500 mb-2">Attach a receipt, check image, or Zelle/Venmo screenshot (image or PDF).</p>
      <div className="flex items-center gap-2">
        <label
          className={`inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-md border cursor-pointer ${
            readOnly || uploading ? "opacity-60 cursor-not-allowed" : "hover:bg-gray-50"
          }`}
          data-testid={`label-${idPrefix}-proof-upload`}
        >
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
          {uploading ? "Uploading..." : "Add files"}
          <input
            type="file" accept="image/*,application/pdf" multiple className="hidden"
            disabled={readOnly || uploading}
            onChange={(e) => { onSelect(e.target.files); e.target.value = ""; }}
            data-testid={`input-${idPrefix}-proof`}
          />
        </label>
      </div>
      {urls.length > 0 && (
        <div className="mt-2" data-testid={`list-${idPrefix}-proof`}>
          <AttachmentPreviews urls={urls} idPrefix={`${idPrefix}-proof`} onRemove={readOnly ? undefined : onRemove} />
        </div>
      )}
    </div>
  );
}

export function ApproveAndPayModal({
  task,
  open,
  onClose,
  mode = "approve",
}: {
  task: OperationsTask | null;
  open: boolean;
  onClose: () => void;
  // Task #4284 — "archive" opens the popup in historic-close mode: manual tab
  // only, defaults to $0, never charges the host, and flags the request so the
  // backend skips the owner gate + the vendor "you've been paid" email.
  mode?: "approve" | "archive";
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { canEdit: __canEditArea } = usePermissions();
  const readOnly = !__canEditArea("maintenance");

  const [overallRating, setOverallRating] = useState(0);
  const [qualityRating, setQualityRating] = useState(0);
  const [communicationRating, setCommunicationRating] = useState(0);
  const [timelinessRating, setTimelinessRating] = useState(0);
  const [professionalismRating, setProfessionalismRating] = useState(0);
  const [showCategoryRatings, setShowCategoryRatings] = useState(false);
  const [publicComment, setPublicComment] = useState("");
  const [privateNote, setPrivateNote] = useState("");
  const [shareHostName, setShareHostName] = useState(false);
  const [paymentRail, setPaymentRail] = useState<"stripe" | "mercury_ach">("stripe");
  const [paymentMode, setPaymentMode] = useState<"platform" | "manual">("platform");
  const [manualAmount, setManualAmount] = useState("");
  const [manualMethod, setManualMethod] = useState("");
  const [manualNote, setManualNote] = useState("");
  const [manualProofUrls, setManualProofUrls] = useState<string[]>([]);
  const [manualProofUploading, setManualProofUploading] = useState(false);

  const betaAccessQuery = useQuery<string[]>({ queryKey: ["/api/app/beta/access"] });
  const hasMercuryBeta = (betaAccessQuery.data || []).includes("mercury_vendor_pay");
  const mercuryConnectionQuery = useQuery<{ status: string } | null>({
    queryKey: ["/api/app/mercury-connection"],
    enabled: hasMercuryBeta,
  });
  const hasMercuryConnection = !!mercuryConnectionQuery.data && mercuryConnectionQuery.data.status === "active";
  const mercuryRailAvailable = hasMercuryBeta && hasMercuryConnection;

  const hasCardsBeta = (betaAccessQuery.data || []).includes("mercury_card_provisioning");
  const mercuryCardsQuery = useQuery<{ cards: MercuryCardLite[] }>({
    queryKey: ["/api/app/mercury/cards"],
    enabled: hasCardsBeta && !!task,
  });

  const vendorsQuery = useQuery<Vendor[]>({ queryKey: ["/api/app/operations/vendors"] });
  const propertiesQuery = useQuery<Property[]>({ queryKey: ["/api/app/properties"] });

  type CardRow = { id: number; isDefault: boolean; stripePaymentMethodId?: string | null; brand?: string | null; last4?: string | null; nickname?: string | null };
  const cardsQuery = useQuery<CardRow[]>({ queryKey: ["/api/app/operations/vendor-credit-cards"] });
  const propertyCardAssignmentsQuery = useQuery<{ propertyId: number; vendorCreditCardId: number }[]>({
    queryKey: ["/api/app/operations/property-card-assignments"],
  });

  const resetForm = () => {
    setOverallRating(0);
    setQualityRating(0);
    setCommunicationRating(0);
    setTimelinessRating(0);
    setProfessionalismRating(0);
    setShowCategoryRatings(false);
    setPublicComment("");
    setPrivateNote("");
    setShareHostName(false);
    setPaymentRail("stripe");
    setPaymentMode("platform");
    setManualAmount("");
    setManualMethod("");
    setManualNote("");
    setManualProofUrls([]);
    setManualProofUploading(false);
  };

  // Initialize the active tab + amount when the task identity or mode changes.
  // Task #4284 — Archive mode is a $0 historic close: force the manual tab and
  // default the amount to $0. Otherwise, when the task can't be charged through
  // PadPulse (no vendor or no price), still default to the manual tab; the
  // charge tab is disabled below. This deliberately does NOT depend on the card
  // queries: re-running it when card data resolved asynchronously snapped the
  // tab back to "platform" and clobbered a user's manual-tab switch.
  useEffect(() => {
    if (!task) return;
    const chargeable = !!task.vendorId && (task.priceCents ?? 0) > 0;
    if (mode === "archive") {
      setPaymentMode("manual");
      setManualAmount("0.00");
    } else {
      setPaymentMode(chargeable ? "platform" : "manual");
      setManualAmount((netVendorPayoutAmount(task.priceCents || 0) / 100).toFixed(2));
    }
  }, [task?.id, mode]);

  // Pre-fill the manual payment method from the property's assigned card or the
  // default card once the card data has loaded.
  useEffect(() => {
    if (!task) return;
    const assignments = propertyCardAssignmentsQuery.data || [];
    const cards = cardsQuery.data || [];
    const assigned = task.propertyId != null
      ? assignments.find((a) => a.propertyId === task.propertyId)
      : undefined;
    if (assigned && cards.some((c) => c.id === assigned.vendorCreditCardId)) {
      setManualMethod(`card:${assigned.vendorCreditCardId}`);
    } else {
      const defaultCard = cards.find((c) => c.isDefault);
      setManualMethod(defaultCard ? `card:${defaultCard.id}` : "");
    }
  }, [task?.id, propertyCardAssignmentsQuery.data, cardsQuery.data]);

  const uploadProofFiles = async (files: FileList | null): Promise<string[]> => {
    if (!files || files.length === 0) return [];
    const uploaded: string[] = [];
    for (const file of Array.from(files)) {
      const r = await fetch("/api/app/operations/manual-payments/upload-url", {
        method: "POST", credentials: "include",
      });
      if (!r.ok) throw new Error("Could not start the upload. Please try again.");
      const { uploadUrl, objectPath } = await r.json();
      const put = await fetch(uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": uploadMimeType(file) },
        body: file,
      });
      if (!put.ok) throw new Error(`Upload failed for ${file.name}`);
      uploaded.push(annotateAttachmentUrl(objectPath, file.name));
    }
    return uploaded;
  };

  const handleManualProofSelect = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setManualProofUploading(true);
    try {
      const next = await uploadProofFiles(files);
      setManualProofUrls((prev) => [...prev, ...next]);
    } catch (e: any) {
      toast({ title: "Couldn't upload proof", description: parseErrorMessage(e, "Please try again."), variant: "destructive" });
    } finally {
      setManualProofUploading(false);
    }
  };

  const approveMutation = useMutation({
    mutationFn: async ({ review, paymentRail: rail }: { review: any; paymentRail?: "stripe" | "mercury_ach" }) => {
      if (!task) throw new Error("No task selected");
      const res = await apiRequest("POST", `/api/app/operations/approvals/${task.id}/approve`, { review, paymentRail: rail });
      return res.json();
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/operations/approvals"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/operations/tasks"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/operations/payment-transactions"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/tasks"] });
      onClose();
      resetForm();
      if (data?.mercuryApprovalUrl) {
        try { window.open(data.mercuryApprovalUrl, "_blank", "noopener,noreferrer"); } catch { }
      }
      const payment = data?.payment;
      if (!payment) {
        toast({ title: "Task approved" });
      } else if (payment.status === "succeeded") {
        toast({ title: "Task approved and payment charged" });
      } else if (payment.status === "awaiting_mercury_approval") {
        toast({ title: "Approved — confirm in Mercury", description: "We staged an ACH payment in your Mercury account. Open Mercury to approve & send it." });
      } else if (payment.status === "requires_3ds") {
        toast({ title: "Approved — payment needs verification", description: "Your bank wants to verify this payment. Open the Billing page to confirm.", variant: "destructive" });
      } else if (payment.status === "processing") {
        toast({ title: "Approved — payment processing", description: "Your bank is still processing the charge. We'll update the status shortly." });
      } else {
        toast({ title: "Approved — payment failed", description: payment.failureMessage || "We couldn't charge your card on file. Open the Billing page to fix it.", variant: "destructive" });
      }
    },
    onError: (e: any) => {
      toast({ title: "Error", description: parseErrorMessage(e, "Couldn't approve this task. Please try again."), variant: "destructive" });
    },
  });

  const createManualPaymentMutation = useMutation({
    mutationFn: async (body: any) => {
      if (!task) throw new Error("No task selected");
      const res = await apiRequest("POST", `/api/app/operations/approvals/${task.id}/manual-payment`, body);
      return res.json();
    },
    onSuccess: (_data, variables: any) => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/operations/approvals"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/operations/tasks"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/operations/manual-payments"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/tasks"] });
      onClose();
      resetForm();
      // The backend suppresses the vendor "you've been paid" email on a $0 archive
      // close — keep the toast copy honest about whether an email actually went out.
      const emailSuppressed = variables?.archive === true && (variables?.amountCents ?? 0) === 0;
      toast({
        title: emailSuppressed ? "Task closed" : "Manual payment recorded",
        description: emailSuppressed
          ? "Moved to Paid / Closed with no host charge. The vendor was not emailed."
          : "The vendor has been notified by email.",
      });
    },
    onError: (e: any) => {
      toast({ title: "Couldn't record payment", description: parseErrorMessage(e, "Please try again."), variant: "destructive" });
    },
  });

  const inFlight = approveMutation.isPending || createManualPaymentMutation.isPending;

  const handleApprove = () => {
    if (!task) return;
    if (overallRating > 0 && overallRating < 2 && !privateNote) return;
    approveMutation.mutate({
      paymentRail: mercuryRailAvailable ? paymentRail : undefined,
      review: {
        overallRating,
        qualityRating: qualityRating || null,
        communicationRating: communicationRating || null,
        timelinessRating: timelinessRating || null,
        professionalismRating: professionalismRating || null,
        publicComment: publicComment || null,
        privateNote: privateNote || null,
        shareHostName,
      },
    });
  };

  const handleManualSubmit = () => {
    if (!task) return;
    const parsedMethod = parseManualMethod(manualMethod);
    const amountCents = dollarsToCents(manualAmount);
    if (!parsedMethod || amountCents == null) return;
    const review = overallRating > 0 ? {
      overallRating,
      qualityRating: qualityRating || null,
      communicationRating: communicationRating || null,
      timelinessRating: timelinessRating || null,
      professionalismRating: professionalismRating || null,
      publicComment: publicComment || null,
      privateNote: privateNote || null,
      shareHostName,
    } : undefined;
    createManualPaymentMutation.mutate({
      amountCents,
      method: parsedMethod.method,
      cardId: parsedMethod.cardId,
      note: manualNote || null,
      proofUrls: manualProofUrls,
      ...(review ? { review } : {}),
      // Task #4284 — Archive/historic close: skip the owner gate + the "you've
      // been paid" email when nothing is actually paid.
      ...(mode === "archive" ? { archive: true } : {}),
    });
  };

  if (!task) return null;

  const vendorMap = new Map((vendorsQuery.data || []).map((v) => [v.id, v]));
  const propertyMap = new Map((propertiesQuery.data || []).map((p) => [p.id, p]));
  const vendor = task.vendorId ? vendorMap.get(task.vendorId) : null;
  const property = task.propertyId ? propertyMap.get(task.propertyId) : null;
  const vendorAmount = task.priceCents || 0;
  // Task #4284 — Charging through PadPulse needs both a vendor and a non-zero
  // price. Without them only the manual tab is usable (e.g. closing a historic
  // task at $0). Keep this in sync with the tab default in the effect above.
  const canChargeThroughPlatform = !!task.vendorId && vendorAmount > 0;
  const breakdown = computeVendorFeeBreakdown(vendorAmount);
  const fee = breakdown.padpulseFeeCents;
  const total = breakdown.hostTotalCents;

  return (
    <ApproveTaskDialog
      open={open}
      onOpenChange={(next) => { if (!next) onClose(); }}
      inFlight={inFlight}
    >
      <DialogHeader>
        <DialogTitle>{mode === "archive" ? "Archive & Close" : "Approve Payment"}</DialogTitle>
      </DialogHeader>
      <div className="space-y-4">
        <div>
          <p className="font-medium">{task.title}</p>
          <p className="text-sm text-gray-500">{property?.name} · {vendor?.name}</p>
        </div>

        <div className="space-y-1 text-sm border-t pt-3">
          <div className="mb-2 pb-2 border-b">
            <PriceBreakdownLines task={task} testId="section-modal-price-breakdown" />
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Vendor receives:</span>
            <span data-testid="text-modal-vendor-payout">{formatCents(breakdown.vendorPayoutCents)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">PadPulse fee:</span>
            <span data-testid="text-modal-padpulse-fee">{formatCents(fee)}</span>
          </div>
          <div className="flex justify-between font-semibold border-t pt-1">
            <span>Total charge:</span>
            <span data-testid="text-modal-host-total">{formatCents(total)}</span>
          </div>
        </div>

        <Tabs value={paymentMode} onValueChange={(v) => setPaymentMode(v as "platform" | "manual")} className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger
              value="platform"
              disabled={mode === "archive" || !canChargeThroughPlatform}
              title={
                mode === "archive"
                  ? "Archiving closes the task with a $0 manual payment — no host charge."
                  : !canChargeThroughPlatform
                    ? "Add a vendor and a price to charge through PadPulse. You can still record a manual payment."
                    : undefined
              }
              data-testid="tab-pay-platform"
            >
              Pay with PadPulse
            </TabsTrigger>
            <TabsTrigger value="manual" data-testid="tab-pay-manual">Add Manual Payment</TabsTrigger>
          </TabsList>

          <TabsContent value="platform" className="space-y-4 mt-4">
            {hasCardsBeta && (() => {
              const card = (mercuryCardsQuery.data?.cards || []).find(
                (c) => c.assignedPropertyId != null && c.assignedPropertyId === task.propertyId,
              );
              if (!card) return null;
              const isFrozen = (card.mercuryCardStatus ?? "").toLowerCase() === "frozen";
              return (
                <div className="border-t pt-3 space-y-2" data-testid="section-mercury-card">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <CreditCard className="h-4 w-4 text-gray-500 shrink-0" />
                      <span className="text-sm font-medium truncate" data-testid="text-mercury-card-name">
                        {card.nickname || "Mercury card"}
                      </span>
                      <span className="text-sm text-gray-500 shrink-0" data-testid="text-mercury-card-last4">
                        •••• {card.last4 ?? "****"}
                      </span>
                    </div>
                    <StatusBadge status={card.mercuryCardStatus} />
                  </div>
                  {isFrozen && (
                    <div className="flex items-start gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-800" data-testid="warning-mercury-card-frozen">
                      <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                      <span>Card is frozen — unfreeze before approving.</span>
                    </div>
                  )}
                </div>
              );
            })()}

            {mercuryRailAvailable && (() => {
              const vendorHasAch = !!(vendor && (vendor as any).achRoutingNumber && (vendor as any).achAccountNumber);
              return (
                <div className="border-t pt-3 space-y-2" data-testid="section-rail-picker">
                  <label className="text-sm font-medium text-gray-700 block">Pay via</label>
                  <Select value={paymentRail} onValueChange={(v) => setPaymentRail(v as "stripe" | "mercury_ach")}>
                    <SelectTrigger className="w-full" data-testid="select-payment-rail">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="stripe">Card on file (Stripe)</SelectItem>
                      <SelectItem value="mercury_ach" disabled={!vendorHasAch}>
                        Mercury ACH {vendorHasAch ? "" : "— add bank info on vendor first"}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  {paymentRail === "mercury_ach" && (
                    <p className="text-xs text-gray-500" data-testid="text-mercury-help">
                      We'll stage a Send Money request in your Mercury account. You'll approve &amp; send it from Mercury — the vendor receives the full amount via ACH.
                    </p>
                  )}
                </div>
              );
            })()}

            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium text-sm">Rate this job</span>
                <span className="text-xs text-gray-400">(optional)</span>
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Overall:</span>
                  <StarRating value={overallRating} onChange={setOverallRating} />
                </div>
                <button disabled={readOnly}
                  onClick={() => setShowCategoryRatings(!showCategoryRatings)}
                  className="text-xs text-blue-600 hover:text-blue-700"
                  data-testid="button-toggle-categories"
                >
                  {showCategoryRatings ? "Hide" : "Show"} category ratings ▼
                </button>
                {showCategoryRatings && (
                  <div className="space-y-2 pl-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">Quality:</span>
                      <StarRating value={qualityRating} onChange={setQualityRating} />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">Communication:</span>
                      <StarRating value={communicationRating} onChange={setCommunicationRating} />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">Timeliness:</span>
                      <StarRating value={timelinessRating} onChange={setTimelinessRating} />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">Professionalism:</span>
                      <StarRating value={professionalismRating} onChange={setProfessionalismRating} />
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div>
              <label className="text-sm text-gray-600 block mb-1">Comment (optional, public on vendor profile):</label>
              <Textarea
                value={publicComment}
                onChange={(e) => setPublicComment(e.target.value)}
                rows={3}
                data-testid="textarea-public-comment"
              />
            </div>

            {overallRating > 0 && overallRating < 2 && (
              <div>
                <label className="text-sm text-red-600 block mb-1">Private note (required for low ratings):</label>
                <Textarea
                  value={privateNote}
                  onChange={(e) => setPrivateNote(e.target.value)}
                  rows={2}
                  data-testid="textarea-private-note"
                />
              </div>
            )}

            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={shareHostName} onCheckedChange={(v) => setShareHostName(!!v)} />
              Share my name with this review
            </label>

            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button
                className="bg-brand hover:bg-brand-hover"
                onClick={handleApprove}
                disabled={readOnly || (overallRating > 0 && overallRating < 2 && !privateNote) || approveMutation.isPending}
                data-testid="button-confirm-approve"
              >
                {approveMutation.isPending ? "Processing..." : "Confirm & Pay"}
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="manual" className="space-y-4 mt-4">
            <p className="text-sm text-gray-500" data-testid="text-manual-help">
              {mode === "archive"
                ? "Close this task with no host charge — use $0 for tasks done before PadPulse, or enter what you actually paid off-platform. This moves the task to Paid / Closed and creates a paper trail. The vendor is not emailed for a $0 close."
                : "Record a payment you already made to the vendor off-platform (cash, check, Zelle, Venmo, or a card on file). This creates a paper trail and emails the vendor — no charge is processed through PadPulse."}
            </p>

            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">Amount paid</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">$</span>
                <input
                  type="number" min="0" step="0.01" value={manualAmount}
                  onChange={(e) => setManualAmount(e.target.value)}
                  className="w-full rounded-md border border-gray-300 pl-7 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand"
                  data-testid="input-manual-amount"
                />
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">Payment method</label>
              <Select value={manualMethod} onValueChange={setManualMethod}>
                <SelectTrigger className="w-full" data-testid="select-manual-method">
                  <SelectValue placeholder="Select method" />
                </SelectTrigger>
                <SelectContent>
                  {(cardsQuery.data || []).map((c) => (
                    <SelectItem key={c.id} value={`card:${c.id}`} data-testid={`option-manual-card-${c.id}`}>
                      {c.nickname || c.brand || "Card"} •••• {c.last4 ?? "****"}{c.isDefault ? " (default)" : ""}
                    </SelectItem>
                  ))}
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="check">Check</SelectItem>
                  <SelectItem value="zelle">Zelle</SelectItem>
                  <SelectItem value="venmo">Venmo</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">Note (optional)</label>
              <Textarea
                value={manualNote}
                onChange={(e) => setManualNote(e.target.value)}
                rows={2}
                placeholder="e.g. check #1234, paid 6/14"
                data-testid="textarea-manual-note"
              />
            </div>

            <ManualProofField
              urls={manualProofUrls}
              uploading={manualProofUploading}
              readOnly={readOnly}
              onSelect={handleManualProofSelect}
              onRemove={(idx) => setManualProofUrls((prev) => prev.filter((_, i) => i !== idx))}
              idPrefix="manual"
            />

            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button
                className="bg-brand hover:bg-brand-hover"
                onClick={handleManualSubmit}
                disabled={readOnly || !manualMethod || dollarsToCents(manualAmount) == null || createManualPaymentMutation.isPending}
                data-testid="button-confirm-manual-payment"
              >
                {createManualPaymentMutation.isPending
                  ? (mode === "archive" ? "Closing..." : "Recording...")
                  : (mode === "archive" ? "Archive & Close" : "Add Manual Payment")}
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </ApproveTaskDialog>
  );
}
